import React from 'react';
import {
  Title2,
  Text,
  Button,
  Caption1,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogActions,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Edit20Regular, Delete20Regular } from '@fluentui/react-icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMsalAuth } from '../../hooks/useMsalAuth';
import { useEnvironment } from '../../contexts/AppContext';
import { BackendsService, initializeTRAPIClient } from '../../services/api';
import { createTmdsClient } from '../../services/tmds';
import { getErrorMessage } from '../../utils/errors';
import { ExpandableTable } from '../../components/ExpandableTable';
import type { Column } from '../../components/ExpandableTable';
import DetailsFlyout from '../../components/DetailsFlyout';
import BackendForm from '../../components/forms/BackendForm';
import type { BackendCreateInput, BackendObject, BackendUpdateInput } from '../../types/trapi';

const useStyles = makeStyles({
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: tokens.spacingVerticalM },
  detail: { display: 'grid', gridTemplateColumns: '120px 1fr', gap: tokens.spacingVerticalXS, alignItems: 'start', '@media (max-width: 600px)': { gridTemplateColumns: '1fr' } },
  label: { color: tokens.colorNeutralForeground3 },
  errorText: { color: tokens.colorPaletteRedForeground1 },
  errorBlock: { color: tokens.colorPaletteRedForeground1, display: 'block', marginBottom: tokens.spacingVerticalS },
  expandedContent: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  buttonRow: { display: 'flex', justifyContent: 'flex-end', gap: tokens.spacingHorizontalS, alignItems: 'center' },
  deleteGrid: { display: 'grid', gap: tokens.spacingVerticalSNudge },
  modelsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: `2px ${tokens.spacingHorizontalM}`,
  },
  modelName: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
});

export default function BackendsPage() {
  const styles = useStyles();
  const { currentEnvironment } = useEnvironment();
  const { getToken, account } = useMsalAuth();
  const qc = useQueryClient();

  React.useEffect(() => { initializeTRAPIClient({ environment: currentEnvironment }); }, [currentEnvironment]);

  const query = useQuery<BackendObject[]>({
    queryKey: ['admin', 'backends', currentEnvironment],
    queryFn: async () => {
      const token = await getToken();
      const result = await BackendsService.getBackends({ environment: currentEnvironment, accessToken: token });
      return Array.isArray(result) ? result : [];
    },
    enabled: !!account,
    staleTime: 60_000,
  });

  const tmds = React.useMemo(() => createTmdsClient(getToken), [getToken]);

  // Pathmappings data — per-backend model info
  type PathMappingDeployment = { Name?: string; name?: string; [k: string]: unknown };
  type PathMappingEndpoint = { name?: string; deployments?: PathMappingDeployment[] | PathMappingDeployment; [k: string]: unknown };
  type PathMappingEntry = { apiPath?: string; endpoints?: PathMappingEndpoint[]; [k: string]: unknown };

  const pathmappingsQuery = useQuery<PathMappingEntry[]>({
    queryKey: ['pathmappings'],
    queryFn: () => tmds.getPathMappings<PathMappingEntry[]>(),
    enabled: !!account,
    staleTime: 120_000,
    retry: 1,
  });

  // For a backend name, find its actual deployments from pathmappings
  const getBackendModels = React.useCallback((backendName: string) => {
    const data = pathmappingsQuery.data;
    if (!data || !Array.isArray(data)) return [];
    const normName = backendName.toLowerCase();
    const modelSet = new Set<string>();
    for (const entry of data) {
      for (const ep of entry.endpoints ?? []) {
        if ((ep.name ?? '').toLowerCase() === normName) {
          let deps = ep.deployments;
          if (!deps) continue;
          if (!Array.isArray(deps)) deps = [deps];
          for (const d of deps) {
            const name = d.Name || d.name;
            if (name && typeof name === 'string' && name.trim()) modelSet.add(name.trim());
          }
        }
      }
    }
    return [...modelSet].sort();
  }, [pathmappingsQuery.data]);

  const allItems = query.data ?? [];
  const existingNames = React.useMemo(() => allItems.map(b => b.name), [allItems]);

  // Flyout
  const [flyoutOpen, setFlyoutOpen] = React.useState(false);
  const [flyoutMode, setFlyoutMode] = React.useState<'create' | 'edit'>('create');
  const [editing, setEditing] = React.useState<BackendObject | null>(null);

  // Delete
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [target, setTarget] = React.useState<BackendObject | null>(null);

  const createMut = useMutation({
    mutationFn: async (data: BackendCreateInput) => {
      const token = await getToken();
      return BackendsService.createBackend({ environment: currentEnvironment, accessToken: token, data });
    },
    onSuccess: async () => {
      setFlyoutOpen(false);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['admin', 'backends', currentEnvironment] }),
        qc.invalidateQueries({ queryKey: ['backends', 'stats', currentEnvironment] }),
      ]);
    },
    onError: (error: unknown) => { console.error('Create backend failed:', getErrorMessage(error)); },
  });

  const updateMut = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: BackendUpdateInput }) => {
      const token = await getToken();
      return BackendsService.updateBackend({ environment: currentEnvironment, accessToken: token, id, data });
    },
    onSuccess: async () => {
      setFlyoutOpen(false);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['admin', 'backends', currentEnvironment] }),
        qc.invalidateQueries({ queryKey: ['backends', 'stats', currentEnvironment] }),
      ]);
    },
    onError: (error: unknown) => { console.error('Update backend failed:', getErrorMessage(error)); },
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      const token = await getToken();
      return BackendsService.deleteBackend({ id, environment: currentEnvironment, accessToken: token });
    },
    onSuccess: async () => {
      setDeleteOpen(false); setTarget(null);
      await qc.invalidateQueries({ queryKey: ['admin', 'backends', currentEnvironment] });
    },
    onError: (error: unknown) => { console.error('Delete backend failed:', getErrorMessage(error)); },
  });

  const isFormLoading = createMut.isPending || updateMut.isPending;

  const handleFormSubmit = async (data: BackendCreateInput | BackendUpdateInput) => {
    const isCreatePayload = 'name' in data && data.ci === 'BACKEND';
    if (flyoutMode === 'create') {
      if (!isCreatePayload) throw new Error('Create mode requires a complete BACKEND payload.');
      await createMut.mutateAsync(data);
    } else if (editing?.id) {
      if (isCreatePayload) throw new Error('Edit mode cannot submit backend identity fields.');
      await updateMut.mutateAsync({ id: editing.id, data });
    }
  };

  const openCreate = () => { setEditing(null); setFlyoutMode('create'); setFlyoutOpen(true); };
  const openEdit = (b: BackendObject) => { setEditing(b); setFlyoutMode('edit'); setFlyoutOpen(true); };

  const columns: Column<BackendObject>[] = React.useMemo(() => [
    { key: 'name', label: 'Name', render: (b) => <Text weight="semibold">{b.name}</Text>, getValue: (b) => b.name, sortable: true },
    { key: 'url', label: 'URL', render: (b) => <Caption1 title={b.url} style={{ maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{b.url}</Caption1>, getValue: (b) => b.url, sortable: true, hideOnMobile: true },
    { key: 'authType', label: 'Auth Type', render: (b) => b.authType || '-', getValue: (b) => b.authType || '', sortable: true, filterable: true, hideOnMobile: true },
    { key: 'backendType', label: 'Type', render: (b) => b.backendType || '-', getValue: (b) => b.backendType || '', sortable: true, filterable: true },
  ], []);

  const renderExpanded = (b: BackendObject) => {
    const models = getBackendModels(b.name);
    return (
    <div className={styles.expandedContent}>
      <div className={styles.buttonRow}>
        <Button size="small" appearance="subtle" icon={<Edit20Regular />} onClick={(e) => { e.stopPropagation(); openEdit(b); }} aria-label="Edit backend">Edit</Button>
        <Button size="small" appearance="outline" icon={<Delete20Regular />} disabled
          title="Backend retirement is unavailable until the generation-aware retirement flow is implemented."
          style={{ color: tokens.colorPaletteRedForeground1, borderColor: tokens.colorPaletteRedForeground1 }}
          onClick={(e) => { e.stopPropagation(); setTarget(b); setDeleteOpen(true); }} aria-label="Delete backend">Delete</Button>
      </div>
      <div className={styles.detail}>
        <Text className={styles.label}>Full URL</Text><code style={{ wordBreak: 'break-all' }}>{b.url}</code>
        <Text className={styles.label}>Auth Type</Text><Text>{b.authType || '-'}</Text>
        <Text className={styles.label}>Backend Type</Text><Text>{b.backendType || '-'}</Text>
        {b.keyVaultUrl && <><Text className={styles.label}>Key Vault</Text><code style={{ wordBreak: 'break-all' }}>{b.keyVaultUrl}</code></>}
        {b.ownership?.owner && <><Text className={styles.label}>Owner</Text><Text>{b.ownership.owner}</Text></>}
        {b.ownership?.team && <><Text className={styles.label}>Team</Text><Text>{b.ownership.team}</Text></>}
        {b.properties?.subscriptionId && <><Text className={styles.label}>Subscription</Text><Caption1>{b.properties.subscriptionId}</Caption1></>}
        {b.properties?.resourceGroupName && <><Text className={styles.label}>Resource Group</Text><Caption1>{b.properties.resourceGroupName}</Caption1></>}
        {b.properties?.accountName && <><Text className={styles.label}>Account Name</Text><Caption1>{b.properties.accountName}</Caption1></>}
      </div>
      {models.length > 0 && (
        <div>
          <Text weight="semibold" size={200} style={{ marginBottom: 4, display: 'block' }}>
            Models ({models.length})
          </Text>
          <div className={styles.modelsGrid}>
            {models.map(m => <span key={m} className={styles.modelName} title={m}>{m}</span>)}
          </div>
        </div>
      )}
      {pathmappingsQuery.isLoading && <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Loading models…</Caption1>}
    </div>
  );
  };

  return (
    <div>
      <div className={styles.header}>
        <Title2>Backends</Title2>
        <Button appearance="primary" onClick={openCreate}>New Backend</Button>
      </div>

      {query.error ? (
        <Text className={styles.errorText}>{getErrorMessage(query.error)}</Text>
      ) : (
        <ExpandableTable<BackendObject>
          columns={columns}
          items={allItems}
          getRowKey={(b) => b.id || b.name}
          renderExpanded={renderExpanded}
          loading={query.isLoading}
          emptyMessage="No backends configured."
          searchPlaceholder="Search backends…"
          defaultSortKey="name"
        />
      )}

      <DetailsFlyout
        open={flyoutOpen}
        onClose={() => setFlyoutOpen(false)}
        title={flyoutMode === 'create' ? 'Create Backend' : 'Edit Backend'}
        size="medium"
        footer={
          <>
            <Button appearance="secondary" onClick={() => setFlyoutOpen(false)} disabled={isFormLoading}>Cancel</Button>
            <Button appearance="primary" type="submit" form="backend-edit-form" disabled={isFormLoading}>
              {isFormLoading ? 'Saving…' : flyoutMode === 'create' ? 'Create Backend' : 'Update Backend'}
            </Button>
          </>
        }
      >
        {(createMut.isError || updateMut.isError) && (
          <Text className={styles.errorBlock}>
            {((createMut.error || updateMut.error) as Error)?.message}
          </Text>
        )}
        <BackendForm
          formId="backend-edit-form"
          hideActions
          mode={flyoutMode}
          initialData={editing || undefined}
          existingNames={existingNames}
          isLoading={isFormLoading}
          onSubmit={handleFormSubmit}
          onCancel={() => setFlyoutOpen(false)}
        />
      </DetailsFlyout>

      <Dialog open={deleteOpen} onOpenChange={(_, d) => setDeleteOpen(!!d.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Confirm Delete</DialogTitle>
            <div className={styles.deleteGrid}>
              <Text>Delete backend:</Text>
              <Text weight="semibold">{target?.name}</Text>
              <Text>This action cannot be undone.</Text>
            </div>
            {deleteMut.isError && <Text className={styles.errorText}>{getErrorMessage(deleteMut.error)}</Text>}
            <DialogActions>
              <Button appearance="secondary" onClick={() => setDeleteOpen(false)} disabled={deleteMut.isPending}>Cancel</Button>
              <Button appearance="primary" onClick={() => target?.id && deleteMut.mutate(target.id)} disabled={deleteMut.isPending || !target?.id}>
                {deleteMut.isPending ? 'Deleting…' : 'Delete'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
